import * as React from 'react';
import styled, { css } from 'styled-components';
import groupBy from 'lodash/groupBy';

import { Spoiler } from 'components/common';
import { defaultCompare } from 'logic/DefaultCompare';

const Container = styled.div`
  margin-top: 10px;
  max-height: 500px;
  overflow: auto;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const TableContainer = styled.div<{ $isSimilarFieldsTable: boolean }>(
  ({ theme, $isSimilarFieldsTable }) => css`
    background-color: ${$isSimilarFieldsTable
      ? theme.colors.table.highlightBackground
      : theme.colors.section.filled.background};
    color: ${$isSimilarFieldsTable ? theme.colors.table.highlightText : 'inherit'};
    padding: 5px;
    border-radius: 4px;
  `,
);

const Table = styled.table`
  width: 100%;
  table-layout: fixed;
`;

const Header = styled.div(
  ({ theme }) => css`
    position: sticky;
    background-color: ${theme.colors.cards.background};
    top: 0;
    z-index: 1;
    margin-bottom: 2px;
  `,
);

const FieldRow = styled.tr<{ $isSimilarFieldsTable: boolean }>(
  ({ theme, $isSimilarFieldsTable }) => css`
    &:not(:last-child) {
      border-bottom: 1px solid ${$isSimilarFieldsTable ? theme.colors.table.highlightBorder : theme.colors.misc.divider};
    }

    td {
      padding: 5px;
    }
  `,
);

const StyledSpoiler = styled(Spoiler)`
  word-break: break-word;
`;

const FieldName = styled.div`
  overflow: hidden;
  text-overflow: ellipsis;
`;

type FieldsTableProps = {
  fields: Array<[string, unknown]>;
  renderFieldValue: (fieldName: string, fieldValue: unknown) => React.ReactNode;
  isSimilarFieldsTable?: boolean;
};

const FieldsTable = ({ fields, isSimilarFieldsTable = false, renderFieldValue }: FieldsTableProps) => (
  <TableContainer $isSimilarFieldsTable={isSimilarFieldsTable}>
    <Table>
      <tbody>
        {fields.map(([fieldName, fieldValue]) => (
          <FieldRow key={fieldName} $isSimilarFieldsTable={isSimilarFieldsTable}>
            <td>
              <b>
                <FieldName title={fieldName}>{fieldName}</FieldName>
              </b>
              <StyledSpoiler maxHeight={20} showLabel="Show more" hideLabel="Hide">
                {renderFieldValue(fieldName, fieldValue)}
              </StyledSpoiler>
            </td>
          </FieldRow>
        ))}
      </tbody>
    </Table>
  </TableContainer>
);

type Props = {
  fields: Record<string, unknown>;
  similarFieldsFilterValue: Array<string>;
  renderFieldValue?: (fieldName: string, fieldValue: unknown) => React.ReactNode;
};

const EvidenceFields = ({
  fields,
  similarFieldsFilterValue,
  renderFieldValue = (_, fieldValue: string) => fieldValue,
}: Props) => {
  const groupedFields = groupBy(Object.entries(fields), ([fieldName]) =>
    similarFieldsFilterValue.includes(fieldName) ? 'similar' : 'other',
  ) as Record<'similar' | 'other', Array<[string, unknown]>>;
  const sortedSimilarFields = (groupedFields.similar ?? []).sort(([name1], [name2]) => defaultCompare(name1, name2));
  const sortedOtherFields = (groupedFields.other ?? []).sort(([name1], [name2]) => defaultCompare(name1, name2));

  return (
    <Container>
      {!!sortedSimilarFields.length && (
        <div>
          <Header>Similar fields</Header>
          <FieldsTable fields={sortedSimilarFields} renderFieldValue={renderFieldValue} isSimilarFieldsTable />
        </div>
      )}
      {!!sortedOtherFields.length && (
        <div>
          {!!sortedSimilarFields.length && !!sortedOtherFields.length && <Header>Other fields</Header>}
          <FieldsTable fields={sortedOtherFields} renderFieldValue={renderFieldValue} />
        </div>
      )}
    </Container>
  );
};

export default EvidenceFields;
